import { AuthContext } from "context";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAbility } from "@casl/react";
import { AbilityContext } from "Can";
import changeLogService from "services/change-log-service";
import partCodesService from "services/part-code-service";
import formatPartCode from "helper/formatPartCode";

// @mui material components
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import Divider from '@mui/material/Divider';
import Grid from "@mui/material/Grid";
import Snackbar from "@mui/material/Snackbar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";


// Material Dashboard React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Material Dashboard React context
import { useMaterialUIController } from "context";
import RejectDialog from "cruds/part-code-management/approval-page/rejectDialog";
import ConfirmDialog from "e4acomponents/ConfirmDialog";

import ImageModal from "e4acomponents/ImageModal";

function DetailsTab({
  approveMode
}) {
  const { id } = useParams();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [partCode, setPartCode] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [openAcceptAlert, setOpenAcceptAlert] = useState(false);
  const [openRejectAlert, setOpenRejectAlert] = useState(false);
  const [openImageModal, setOpenImageModal] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmDialogProps, setConfirmDialogProps] = useState({});
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const ability = useAbility(AbilityContext);

  // Fetches the partcode and it's image
  useEffect(() => {
    (async () => {
      const response = await partCodesService.getPartById(id);
      if (response.data.attributes.has_image) {
        const image = await partCodesService.getImage(response.data.attributes.imagePath);
        setImageUrl(image);
      } else {
        setImageUrl("https://ami-spotlight.footbridge.tilkal.com/resources/images/image.jpg");
      }
      setPartCode(response.data.attributes);
    })();
  }, []);

  // Style for table borders
  const borderBottom = {
    borderBottom: ({ borders: { borderWidth }, palette: { light } }) =>
      `${borderWidth[1]} solid ${light.main}`,
  };

  // Function to print the details of a partcode
  const printCard = () => {
    const printButton = document.getElementById("printButton");
    printButton.style.display = "none";
    const printContents = document.getElementById("printableCard").innerHTML;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  // Helper function to generate the detailed description based on the attributes of the partcode
  const detailedDescription = () => {
    let desc = "";
    if (partCode && partCode.attributes) {

      // Add category code at the beginning
      if (partCode.partCategory && partCode.partCategory.categoryCode) {
        desc = partCode.partCategory.categoryCode + " ";
      }

      // Filter category Attributes to only inclues the one tied to the type of the partcode.
      const categoryAttributes = partCode.partCategory.attributes;

      if (categoryAttributes["0"] && categoryAttributes["0"].enableCondAttrs) {
        const typeValue = partCode.attributes[categoryAttributes[0].attribute];
        const keysToDisplay = categoryAttributes[0][typeValue].split(";");
        for (const key in categoryAttributes) {
          if (!keysToDisplay.includes(key) && key !== "0") {
            delete categoryAttributes[key];
          }
        }
      }

      // Iterate over part category attributes
      Object.values(categoryAttributes).forEach(value => {
        if (value.attribute in partCode.attributes) {
          const attValue = partCode.attributes[value.attribute]

          if (value.options) {
            const options = value.options.includes(";") ? value.options.split(";") : [value.options];
            const descriptions = value.description.split(";");
            const optionDict = {};
            for (let i = 0; i < options.length; i++) {
              optionDict[options[i]] = descriptions[i];
            }
            if (value.enableMultiVals) {
              for (const val in attValue) {
                desc += optionDict[attValue[val]] || attValue[val];
                desc += ","
              }
              desc = desc.slice(0, -1) + " "; // Remove last comma and add space
            } else {
              desc += optionDict[attValue] || attValue;
              desc += " ";
            }
          } else if (value.units) {
            const unitValue = partCode.attributes[value.attribute].split(";")[1];
            const trimmedAttValue = partCode.attributes[value.attribute].split(";")[0];
            desc += trimmedAttValue + unitValue + " ";
          } else {
            desc += attValue + " ";
          }
        }
      });

      return desc.trim();
    }
    return "N/A";
  }

  // Helper function to generate the description with category code prefix
  const description = (partCode) => {
    const categoryCode = partCode.partCategory.categoryCode;
    return partCode.description.startsWith(categoryCode)
      ? partCode.description
      : `${categoryCode} ${partCode.description}`;
  }

  // Handles clicking the approve part button
  const clickApproveHandler = async () => {
    try {
      if (!confirm("Are you sure you want to approve this part?")) {
        e.nativeEvent.stopImmediatePropagation();
      } else {

        await partCodesService.approvePart(id);

        const changeLog = {
          data: {
            attributes: {
              partCode: id,
              change: {
                type: "Approved",
                description: "Part Code Approved",
                date: new Date(),
                user: await authContext.getCurrentUser()
              }
            }
          }
        }

        await changeLogService.addChange(changeLog);
        setOpenAcceptAlert(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Handles clicking the reject part button
  const clickRejectHandler = async () => {
    setOpenRejectDialog(true);
  }

  // Redirects to the approval page & resets snackbar alerts
  const redirectToApprovalPage = () => {
    setOpenAcceptAlert(false);
    setOpenRejectAlert(false);
    navigate(-1);
  }

  // Handles closing the reject dialog
  const handleCloseRejectDialog = (status) => {
    setOpenRejectDialog(false);
    if (status === "rejected") {
      setOpenRejectAlert(true);
    }
  }

  // Handles opening the confirm dialog
  const handleOpenConfirmDialog = (title, prompt, id) => {
    setConfirmDialogProps({
      title: title,
      prompt: prompt,
      id: id,
      onClose: handleCloseConfirmDialog
    })
    setOpenConfirmDialog(true);
  }

  // Handles closing the confirm dialog
  const handleCloseConfirmDialog = (response, title, id) => {

    setOpenConfirmDialog(false);

    if (response) {
      if (title === "Mark as Obsolete") {
        editStatus("Obsolete");
      } else if (title === "Reinstate Part") {
        editStatus("Pending Approval");
      }
    }
  }

  // Marks the partcode as obsolete
  const editStatus = async (status) => {
    try {
      const payload = {
        data: {
          attributes: {
            status: status
          },
          logs: {
            user: await authContext.getCurrentUser(),
            comment: `Part status changed to "${status}"`,
          }
        }
      }
      await partCodesService.editStatus(id, payload);
      navigate(-1);
    } catch (error) {
      console.error("Error making part obsolete:", error);
    }
  }


  return (
    <MDBox mt={{ xs: 4, md: 10 }} mb={{ xs: 4, md: 8 }}>
      <Grid container justifyContent="center">

        {/* Part Code Accepted Alert */}
        <Snackbar
          open={openAcceptAlert}
          autoHideDuration={1500}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          onClose={redirectToApprovalPage}
        >
          <Alert severity="success" >
            Part Code Approved Successfully
          </Alert>
        </Snackbar>

        {/* Part Code Rejected Alert */}
        <Snackbar
          open={openRejectAlert}
          autoHideDuration={1500}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          onClose={redirectToApprovalPage}
        >
          <Alert severity="error" >
            Part Code Rejected Successfully
          </Alert>
        </Snackbar>

        {/* Image Modal */}
        <ImageModal
          open={openImageModal}
          onClose={() => setOpenImageModal(false)}
          mediaURL={imageUrl}
        />

        {/* Reject Part Code Dialog */}
        <RejectDialog
          handleClose={handleCloseRejectDialog}
          open={openRejectDialog}
          id={id}
        />

        {/* Confirm Dialog */}
        <ConfirmDialog
          open={openConfirmDialog}
          props={confirmDialogProps}
        />

        <Grid item xs={12} sm={10} md={8}>
          <Card id="printableCard">
            {/* Invoice header */}
            <MDBox p={3}>

              <Grid container spacing={3}>

                {/* Part Code's Image */}
                <Grid item xs={12} md={5}>
                  <MDBox p={5} onClick={() => setOpenImageModal(true)} sx={{ cursor: "pointer" }}>
                    <img
                      src={imageUrl}
                      width="100%"
                    />
                  </MDBox>
                </Grid>

                <Grid item xs={12} md={7}>

                  {/* Title */}
                  <MDBox textAlign="center" mb={3}>
                    <MDTypography variant="h3" fontWeight="medium">
                      {partCode && (formatPartCode(partCode))}
                    </MDTypography>
                  </MDBox>

                  {/* Description */}
                  <Grid container spacing={{ xs: 2, md: 4 }} mb={3}>
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="h6" fontWeight="medium" textAlign={{ xs: "left", md: "right" }}>
                        Description
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="h6" fontWeight="regular">
                        {partCode && (description(partCode))}
                      </MDTypography>
                    </Grid>
                  </Grid>

                  {/* Manufacturer */}
                  <Grid container spacing={{ xs: 2, md: 4 }} mb={3}>
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="h6" fontWeight="medium" textAlign={{ xs: "left", md: "right" }}>
                        Manufacturer
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="h6" fontWeight="regular" >
                        {partCode && (partCode.manufacturer ? (partCode.manufacturer) : "N/A")}
                      </MDTypography>
                    </Grid>
                  </Grid>

                  {/* Manufacturer Part Number */}
                  <Grid container spacing={{ xs: 2, md: 4 }} mb={3}>
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="h6" fontWeight="medium" textAlign={{ xs: "left", md: "right" }}>
                        Manufacturer Part Number
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="h6" fontWeight="regular" >
                        {partCode ? (partCode.mpn ? partCode.mpn[partCode.preferred_mpn] : partCode.manufacturer_part_number) : "N/A"}
                      </MDTypography>
                    </Grid>
                  </Grid>

                  {/* Vendor */}
                  <Grid container spacing={{ xs: 2, md: 4 }} mb={3}>
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="h6" fontWeight="medium" textAlign={{ xs: "left", md: "right" }}>
                        Vendor
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="h6" fontWeight="regular" >
                        {partCode && (partCode.vendors ? (partCode.vendors[`vendor-${partCode.preferred_vendor}`]) : "N/A")}
                      </MDTypography>
                    </Grid>
                  </Grid>

                  {/* Vendor Part Number */}
                  <Grid container spacing={{ xs: 2, md: 4 }} mb={3}>
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="h6" fontWeight="medium" textAlign={{ xs: "left", md: "right" }}>
                        Vendor Part Number
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="h6" fontWeight="regular" >
                        {partCode && (partCode.vendor_part_number ? (partCode.vendor_part_number) : "N/A")}
                      </MDTypography>
                    </Grid>
                  </Grid>

                  {/* Detailed Description */}
                  <Grid container spacing={{ xs: 2, md: 4 }} mb={3}>
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="h6" fontWeight="medium" textAlign={{ xs: "left", md: "right" }}>
                        Detailed Description
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MDTypography variant="h6" fontWeight="regular" >
                        {partCode && (partCode.attributes ? (detailedDescription()) : "N/A")}
                      </MDTypography>
                    </Grid>
                  </Grid>

                </Grid>
              </Grid>

              <Divider />

              {/* Time Stamps */}
              <MDBox mt={{ xs: 5, md: 7 }}>
                <Grid container justifyContent="space-between">
                  <Grid item xs={12} md={4}>
                    <MDTypography
                      variant="h6"
                      color={darkMode ? "text" : "secondary"}
                      fontWeight="regular"
                    >
                      Status :
                    </MDTypography>
                    <MDTypography variant="h5" fontWeight="medium">
                      {partCode && (partCode.status)}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={7} lg={5}>
                    <MDBox
                      width="100%"
                      display="flex"
                      flexDirection={{ xs: "column", md: "row" }}
                      alignItems={{ xs: "flex-start", md: "center" }}
                      textAlign={{ xs: "left", md: "right" }}
                      mt={{ xs: 3, md: 0 }}
                    >
                      <MDBox width="50%">
                        <MDTypography
                          variant="h6"
                          color={darkMode ? "text" : "secondary"}
                          fontWeight="regular"
                        >
                          Last Update :
                        </MDTypography>
                      </MDBox>
                      <MDBox width="50%">
                        <MDTypography variant="h6" fontWeight="medium">
                          {partCode && (partCode.updated_at.split("T")[0])}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                    <MDBox
                      width="100%"
                      display="flex"
                      flexDirection={{ xs: "column", md: "row" }}
                      alignItems={{ xs: "flex-start", md: "center" }}
                      textAlign={{ xs: "left", md: "right" }}
                    >
                      <MDBox width="50%">
                        <MDTypography
                          variant="h6"
                          color={darkMode ? "text" : "secondary"}
                          fontWeight="regular"
                        >
                          Creation Date :
                        </MDTypography>
                      </MDBox>
                      <MDBox width="50%">
                        <MDTypography variant="h6" fontWeight="medium">
                          {partCode && (partCode.created_at.split("T")[0])}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>


            </MDBox>

            <Divider />

            {/* Attributes table */}
            {partCode && partCode.attributes && (
              <MDBox p={3}>
                <MDBox width="100%" overflow="auto">
                  <Table sx={{ minWidth: "32rem" }}>
                    <MDBox component="thead">
                      <TableRow>
                        <MDBox
                          component="th"
                          width={{ xs: "50%", md: "75%" }}
                          py={1.5}
                          px={1}
                          textAlign="left"
                          sx={borderBottom}
                        >
                          <MDTypography variant="h6" color="text" fontWeight="medium">
                            Attributes
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          component="th"
                          py={1.5}
                          pl={3}
                          pr={1}
                          textAlign="left"
                          sx={borderBottom}
                        >
                          <MDTypography variant="h6" color="text" fontWeight="medium">
                            Values
                          </MDTypography>
                        </MDBox>
                      </TableRow>
                    </MDBox>
                    <TableBody>
                      {Object.entries(partCode.partCategory.attributes)
                        .filter(([num, attrObj]) => {

                          const attrName = attrObj.attribute;
                          if (partCode.partCategory.attributes["0"] && partCode.partCategory.attributes["0"].enableCondAttrs) {
                            // Get the type and its value
                            const type = partCode.partCategory.attributes["0"].attribute;
                            const typeValue = partCode.attributes[type];
                            const keysToDisplay = partCode.partCategory.attributes["0"][typeValue].split(";")
                            // Only show if the key is in keysToDisplay and attribute has a value
                            return keysToDisplay.includes(num) && attrName in partCode.attributes;
                          } else {
                            // If no keys to display, show all attributes
                            return attrName in partCode.attributes;
                          }
                        })
                        .map(([num, attrObj], index) => {
                          const attrName = attrObj.attribute;
                          let value = partCode.attributes[attrName];
                          // If value is an array, join with comma
                          if (Array.isArray(value)) {
                            value = value.join(", ");
                          }
                          // If value contains semicolons, join with space
                          if (typeof value === 'string' && value.includes(';')) {
                            value = value.split(';').join(' ');
                          }
                          return (
                            <TableRow key={index}>
                              <MDBox component="td" textAlign="left" p={1} sx={borderBottom}>
                                <MDTypography variant="body2" color="text" fontWeight="regular">
                                  {attrName}
                                </MDTypography>
                              </MDBox>
                              <MDBox
                                component="td"
                                textAlign="left"
                                py={1}
                                pr={1}
                                pl={3}
                                sx={borderBottom}
                              >
                                <MDTypography variant="body2" color="text" fontWeight="regular">
                                  {value}
                                </MDTypography>
                              </MDBox>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </MDBox>
              </MDBox>
            )}

            {partCode && partCode.attributes && (
              <Divider />)}

            {/* Card's Footer */}
            <MDBox p={3} mt={7}>
              <Grid container>

                <Grid item xs={12} lg={12}>
                  <MDBox
                    width="100%"
                    height={{ xs: "auto", md: "100%" }}
                    display="flex"
                    justifyContent={approveMode ? "center" : "flex-end"}
                    mt={{ xs: 2, md: 0 }}
                  >

                    {approveMode && ability.can('access', 'approval-page') && (
                      <>

                        <MDBox mx={1}>
                          <MDButton id="approveButton" variant="gradient" color="success" onClick={clickApproveHandler} size="small">
                            Approve Part Code
                          </MDButton>
                        </MDBox>
                        <MDBox mx={1}>
                          <MDButton id="rejectButton" variant="gradient" color="error" onClick={clickRejectHandler} size="small">
                            Reject Part Code
                          </MDButton>
                        </MDBox>

                      </>
                    )}

                    {!approveMode && ability.can("obsolete", "part-code") &&
                      (partCode &&
                        ["Approved", "Pending Approval", "Rejected"].includes(partCode.status)) &&
                      (
                        <MDBox mx={1}>
                          <MDButton variant="gradient" color="warning" size="small"
                            onClick={() => handleOpenConfirmDialog(
                              "Mark as Obsolete",
                              "Are you sure you want to mark this part as Obsolete?",
                              id
                            )}>

                            Mark as Obsolete
                          </MDButton>
                        </MDBox>
                      )}
                    {!approveMode && ability.can("obsolete", "part-code") &&
                      (partCode && partCode.status === "Obsolete") &&
                      (
                        <MDBox mx={1}>
                          <MDButton variant="gradient" color="success" size="small"
                            onClick={() => handleOpenConfirmDialog(
                              "Reinstate Part",
                              "Are you sure you want to reinstate this part?",
                              id
                            )}>
                            Reinstate Part
                          </MDButton>
                        </MDBox>
                      )}

                    {ability.can("access", "create-inventory-item") &&
                      (partCode && partCode.status === "Approved") && (
                        <MDBox mx={1}>
                          <MDButton variant="gradient" color="info" size="small"
                            onClick={() => navigate(`/inventory-management/create-inventory-item/${partCode ? partCode._id : ""}`)}
                          >
                            Create Inventory Item
                          </MDButton>
                        </MDBox>)}

                    <MDBox mx={1}>
                      <MDButton id="printButton" variant="gradient" color="info" onClick={printCard} size="small">
                        print
                      </MDButton>
                    </MDBox>

                    <MDBox mx={1}>
                      <MDButton
                        variant="gradient"
                        color="info"
                        ml={2}
                        href={partCode && (partCode.vendors ? (partCode.vendors[`link-${partCode.preferred_vendor}`]) : "#")} size="small"
                      >
                        Vendor Page
                      </MDButton>
                    </MDBox>

                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>


  )
};

export default DetailsTab;